// src/services/twonService.js
// 2N Access Commander Integration API Client
// Provides access to 2N intercom and access control device management

import axios from 'axios';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';

// Get base URL from environment or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

// Create axios instance for 2N API calls
const twonAxios = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  withCredentials: true,
});

// Helper function to get token from storage (same as apiClient.js)
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);

      if (encryptedToken) {
        try {
          const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
          if (decryptedToken) {
            return decryptedToken;
          }
        } catch (decryptError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Decryption failed:', decryptError);
          }
        }
      }
    }

    // Fallback to localStorage
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error retrieving token:', error);
    }
    // Last resort
    return localStorage.getItem(key);
  }
};

// Add auth token to requests (async version)
twonAxios.interceptors.request.use(
  async (config) => {
    const token = await getTokenFromStorage('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 2N Access Commander API Client
export const twonApi = {
  // ==================== Configuration ====================

  // Phase 2: Multi-Commander Support

  // Get all Commander configurations
  getAllConfigs: () => twonAxios.get('/admin/twon/configs'),

  // Get specific Commander configuration
  getConfig: (commanderId) => {
    if (commanderId) {
      return twonAxios.get(`/admin/twon/configs/${commanderId}`);
    }
    // Backward compatibility - get first config
    return twonAxios.get('/admin/twon/config');
  },

  // Save 2N Commander configuration
  saveConfig: (configData) => twonAxios.post('/admin/twon/config', configData),

  // Update existing configuration
  updateConfig: (id, configData) => twonAxios.put(`/admin/twon/config/${id}`, configData),

  // Delete Commander configuration
  deleteConfig: (commanderId) => twonAxios.delete(`/admin/twon/configs/${commanderId}`),

  // Test connection to 2N Commander (for new/unsaved config)
  testConnection: (connectionData) => twonAxios.post('/admin/twon/test-connection', connectionData),

  // Test connection for stored Commander by ID
  testCommanderConnection: (commanderId) => twonAxios.post(`/admin/twon/configs/${commanderId}/test`),

  // ==================== Import & Sync ====================

  // Trigger full device import from specific Commander (Phase 2)
  importDevices: (commanderId, options = {}) => {
    // Phase 2: commanderId is required
    return twonAxios.post('/admin/twon/import', { commanderId, ...options });
  },

  // Trigger manual sync - backward compatibility
  syncDevices: () => twonAxios.post('/admin/twon/sync'),

  // Phase 2: Multi-Commander Sync
  syncAllCommanders: () => twonAxios.post('/admin/twon/sync-all'),

  // Sync specific Commander
  syncCommander: (commanderId) => twonAxios.post(`/admin/twon/configs/${commanderId}/sync`),

  // Cancel running sync
  cancelSync: () => twonAxios.post('/admin/twon/cancel-sync'),

  // Get current sync/import status
  getSyncStatus: (commanderId) => {
    if (commanderId) {
      return twonAxios.get(`/admin/twon/sync-status?commanderId=${commanderId}`);
    }
    return twonAxios.get('/admin/twon/sync-status');
  },

  // Get integration statistics
  getStats: () => twonAxios.get('/admin/twon/stats'),

  // ==================== Device Actions ====================

  // Unlock door on a 2N device
  unlockDoor: (deviceId, data = {}) => twonAxios.post(`/api/devices/${deviceId}/twon/door-unlock`, data),

  // Restart a 2N device
  restartDevice: (deviceId, data = {}) => twonAxios.post(`/api/devices/${deviceId}/twon/restart`, data),

  // Get camera stream URL for a device
  getCameraUrl: (deviceId) => twonAxios.get(`/api/devices/${deviceId}/twon/camera-url`),

  // Initiate test call to 2N device via Clearvox
  testCall: (deviceId, data) => twonAxios.post(`/api/devices/${deviceId}/twon/test-call`, data),

  // Get zone code (admin only)
  getZoneCode: (deviceId) => twonAxios.get(`/api/devices/${deviceId}/twon/zone-code`),

  // Change zone code (admin only)
  changeZoneCode: (deviceId, data) => twonAxios.put(`/api/devices/${deviceId}/twon/zone-code`, data),

  // ==================== Device Management ====================

  // Get detailed 2N device information
  getDeviceDetails: (deviceId) => twonAxios.get(`/api/devices/${deviceId}/twon-details`),

  // Sync single device from 2N
  syncSingleDevice: (deviceId) => twonAxios.post(`/api/devices/${deviceId}/sync-from-twon`),

  // Get available Clearvox devices for test calls
  getAvailableClearvox: (deviceId) => twonAxios.get(`/api/devices/${deviceId}/twon/available-clearvox`),

  // Link device to location
  linkToLocation: (deviceId, locationId) => twonAxios.post(`/api/devices/${deviceId}/link-location`, { locationId }),

  // Unlink device from location
  unlinkFromLocation: (deviceId) => twonAxios.delete(`/api/devices/${deviceId}/unlink-location`),

  // ==================== Action Logs ====================

  // Get all action logs (admin)
  getAllActionLogs: (params) => twonAxios.get('/admin/twon/action-logs', { params }),

  // Get action logs for specific device
  getDeviceActionLogs: (deviceId, params) => twonAxios.get(`/api/devices/${deviceId}/twon/action-logs`, { params }),
};

// Helper function to check if device is a 2N device (DeviceType = 13)
export const isTwoNDevice = (device) => {
  if (!device) return false;

  // Check deviceType or deviceTypeModel
  const deviceTypeId = device.deviceType || device.deviceTypeModel?.id;
  return deviceTypeId === 13;
};

// Helper function to extract 2N device capabilities
export const getTwoNCapabilities = (device) => {
  if (!device || !isTwoNDevice(device)) return null;

  const twonConfig = device.config?.twon;
  if (!twonConfig) return null;

  return {
    hasDoor: twonConfig.capabilities?.hasDoor || false,
    hasCamera: twonConfig.capabilities?.hasCamera || false,
    hasRFID: twonConfig.capabilities?.hasRFID || false,
    hasKeypad: twonConfig.capabilities?.hasKeypad || false,
    hasBluetooth: twonConfig.capabilities?.hasBluetooth || false,
  };
};

// Helper function to get 2N device information
export const getTwoNDeviceInfo = (device) => {
  if (!device || !isTwoNDevice(device)) return null;

  const twonConfig = device.config?.twon;
  if (!twonConfig) return null;

  return {
    ipAddress: twonConfig.ip_address,
    serialNumber: twonConfig.serial_number,
    macAddress: twonConfig.mac_address,
    productName: twonConfig.product_name,
    firmwareVersion: twonConfig.firmware_version,
    hardwareVersion: twonConfig.hardware_version,
    online: twonConfig.status?.online || false,
    lastSeen: twonConfig.status?.last_seen,
    uptime: twonConfig.status?.uptime_seconds,
  };
};

// Helper function to check if device has camera
export const deviceHasCamera = (device) => {
  const capabilities = getTwoNCapabilities(device);
  return capabilities?.hasCamera || false;
};

// Helper function to check if device has door control
export const deviceHasDoor = (device) => {
  const capabilities = getTwoNCapabilities(device);
  return capabilities?.hasDoor || false;
};

// Export default API client
export default twonApi;
